import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ArrowLeftIcon } from 'lucide-react';
import { PhoneInput } from '../components/PhoneInput';
import { CodeInput } from '../components/CodeInput';
import { useDriver } from '../context/DriverContext';
import { useFcmToken } from '../hooks/useFcmToken';
import { mapApiResponseToDriverProgress } from '../utils/apiMapping';
import { SearchByPhoneResponse } from '../types/api';
export function LoginPage() {
  const navigate = useNavigate();
  const {
    setDriverData
  } = useDriver();
  const fcmToken = useFcmToken();
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [apiCode, setApiCode] = useState('');
  const [apiResponse, setApiResponse] = useState<SearchByPhoneResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const validatePhone = () => {
    if (phoneNumber.length !== 10) {
      setErrors({
        phone: 'Le numéro doit contenir 10 chiffres'
      });
      return false;
    }
    setErrors({});
    return true;
  };
  const validateCode = () => {
    if (verificationCode.length !== 6) {
      setErrors({
        code: 'Le code doit contenir 6 chiffres'
      });
      return false;
    }
    if (verificationCode !== apiCode) {
      setErrors({
        code: 'Code incorrect, veuillez réessayer'
      });
      return false;
    }
    setErrors({});
    return true;
  };
  const handleContinue = async () => {
    setLoading(true);
    if (step === 1) {
      if (!validatePhone()) {
        setLoading(false);
        return;
      }
      try {
        // Prepare request body with FCM token if available
        const requestBody: any = {
          phone: `+225${phoneNumber}`
        };
        if (fcmToken) {
          requestBody.fcm_token = fcmToken;
          console.log('Sending FCM token with login request:', fcmToken);
        }
        const response = await fetch('https://api.saalal.com/v1/challenge/search-by-phone', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        if (response.status === 200) {
          const data: SearchByPhoneResponse = await response.json();
          setApiCode(String(data.data?.driver?.OTP || ''));
          setApiResponse(data);
          setStep(2);
          setErrors({});
        } else if (response.status === 404) {
          setErrors({
            phone: 'Aucun chauffeur retrouvé avec ce numéro'
          });
        } else {
          setErrors({
            phone: 'Une erreur est survenue, veuillez réessayer'
          });
        }
      } catch (error) {
        setErrors({
          phone: 'Erreur de connexion, veuillez vérifier votre internet'
        });
      }
    } else if (step === 2) {
      if (validateCode()) {
        if (apiResponse) {
          const driverProgress = mapApiResponseToDriverProgress(apiResponse);
          setDriverData(driverProgress);
        }
        navigate('/');
      }
    }
    setLoading(false);
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
      setVerificationCode('');
    }
  };
  const handleResendCode = async () => {
    setLoading(true);
    setErrors({});
    try {
      // Prepare request body with FCM token if available
      const requestBody: any = {
        phone: `+225${phoneNumber}`
      };
      if (fcmToken) {
        requestBody.fcm_token = fcmToken;
      }
      const response = await fetch('https://api.saalal.com/v1/challenge/search-by-phone', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      if (response.status === 200) {
        const data: SearchByPhoneResponse = await response.json();
        setApiCode(String(data.data?.driver?.OTP || ''));
        setApiResponse(data);
      }
    } catch (error) {
      setErrors({
        code: 'Erreur lors du renvoi du code'
      });
    }
    setLoading(false);
  };
  const isStepValid = () => {
    if (step === 1) return phoneNumber.length === 10;
    if (step === 2) return verificationCode.length === 6;
    return false;
  };
  return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-200">
      <div className="w-full max-w-md" data-id="element-201">
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6" data-id="element-202">
          {/* Header with back button */}
          <div className="flex items-center justify-between" data-id="element-203">
            {step > 1 ? <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-204">
                <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-205" />
              </button> : <div data-id="element-206" />}
            <div className="text-sm font-medium text-gray-500" data-id="element-207">
              Étape {step}/2
            </div>
          </div>
          {/* Progress bar */}
          <div className="flex gap-2" data-id="element-208">
            {[1, 2].map(s => <div key={s} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${s <= step ? 'bg-amber-500' : 'bg-gray-200'}`} data-id="element-209" />)}
          </div>
          {/* Step content */}
          <div className="space-y-6 py-4" data-id="element-210">
            {step === 1 && <div className="space-y-4" data-id="element-211">
                <div data-id="element-212">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-213">
                    Bienvenue
                  </h1>
                  <p className="text-gray-600" data-id="element-214">
                    Entrez votre numéro de téléphone pour continuer
                  </p>
                </div>
                <PhoneInput value={phoneNumber} onChange={setPhoneNumber} error={errors.phone} data-id="element-215" />
              </div>}
            {step === 2 && <div className="space-y-4" data-id="element-216">
                <div data-id="element-217">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-218">
                    Vérification
                  </h1>
                  <p className="text-gray-600" data-id="element-219">
                    Un code a été envoyé au{' '}
                    <span className="font-semibold" data-id="element-220">+225 {phoneNumber}</span>
                  </p>
                </div>
                <CodeInput value={verificationCode} onChange={setVerificationCode} error={errors.code} data-id="element-221" />
                <button onClick={handleResendCode} disabled={loading} className="text-sm text-amber-600 hover:text-amber-700 font-medium disabled:opacity-50" data-id="element-222">
                  Renvoyer le code
                </button>
              </div>}
          </div>
          {/* Continue button */}
          <button onClick={handleContinue} disabled={!isStepValid() || loading} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold py-4 px-6 rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md" data-id="element-223">
            {loading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-224" /> : <>
                <span data-id="element-225">{step === 2 ? 'Terminer' : 'Continuer'}</span>
                {step === 2 && <CheckCircleIcon className="w-5 h-5" data-id="element-226" />}
              </>}
          </button>
        </div>
        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6" data-id="element-227">
          En continuant, vous acceptez nos conditions d'utilisation
        </p>
      </div>
    </div>;
}